/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * MarketSection — TAM / SAM / SOM subscriber funnel + revenue sizing.
 *
 * Two visualisation modes (togglable):
 *   1. Bar chart  — grouped bars for TAM · SAM · SOM at all 3 ARPU scenarios
 *   2. Circles    — nested concentric circles with an ARPU scenario switcher
 *
 * All $ figures derived from (accounts × payerRate × ARPU × 12).
 */
import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, Circle } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import InfoTooltip from "../InfoTooltip";

// ── Colour palette ───────────────────────────────────────────────────────────
const funnelColors = [
  { bg: "bg-[#3B82F6]/15", border: "border-[#3B82F6]/30", text: "text-[#60A5FA]", glow: "shadow-[#3B82F6]/20" },
  { bg: "bg-[#A78BFA]/15", border: "border-[#A78BFA]/30", text: "text-[#C4B5FD]", glow: "shadow-[#A78BFA]/20" },
  { bg: "bg-[#4FA9A1]/15", border: "border-[#4FA9A1]/30", text: "text-[#6FCFC5]", glow: "shadow-[#4FA9A1]/20" },
  { bg: "bg-[#F59E0B]/15", border: "border-[#F59E0B]/30", text: "text-[#FCD34D]", glow: "shadow-[#F59E0B]/20" },
];

const barHex = ["#60A5FA", "#F87171", "#A3E635"];
const circleHex = { tam: "#3B82F6", sam: "#A78BFA", som: "#4FA9A1" };

// ── Formatting ───────────────────────────────────────────────────────────────
function fmtDollars(value, currency = "A$") {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${currency}${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${currency}${(value / 1_000).toFixed(0)}K`;
  return `${currency}${value.toFixed(0)}`;
}

function computeRevenue(accounts, payerRate, arpu) {
  return accounts * payerRate * arpu * 12;
}

// ═══════════════════════════════════════════════════════════════════════════════
//  BAR CHART VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const BAR_HEIGHT = 140;

function Bar({ pct, color, label }) {
  const h = Math.max(pct * BAR_HEIGHT, 6);
  return (
    <div className="flex flex-col items-center w-8 sm:w-10 md:w-12">
      <div className="relative w-full flex flex-col justify-end" style={{ height: BAR_HEIGHT + 18 }}>
          <span
          className="text-[7px] sm:text-[10px] font-bold text-white text-center leading-none"
          style={{ position: "absolute", bottom: h + 2, left: "50%", transform: "translateX(-50%)" }}
        >
          {label}
        </span>
        <div
          className="w-full rounded-t-md transition-all duration-700 ease-out"
          style={{
            height: h,
            background: `linear-gradient(to top, ${color}cc, ${color})`,
            boxShadow: `0 0 14px ${color}50, inset 0 1px 0 rgba(255,255,255,0.15)`,
          }}
        />
      </div>
    </div>
  );
}

function BarCluster({ label, bars }) {
  const localMax = Math.max(...bars.map((b) => b.value));
  return (
    <div className="flex flex-col items-center">
      <div className="flex items-end gap-1 sm:gap-1.5">
        {bars.map((b, i) => (
          <Bar key={i} pct={localMax > 0 ? b.value / localMax : 0} color={barHex[i]} label={b.formatted} />
        ))}
      </div>
      <p className="text-xs sm:text-sm text-gray-300 font-semibold mt-3">{label}</p>
    </div>
  );
}

function BarChartView({ groups, arpus, currency }) {
  return (
    <>
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-6">
        {arpus.map((a, i) => (
          <div key={a} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: barHex[i] }} />
            <span className="text-[11px] text-gray-300 font-medium">ARPU {currency}{a}/m</span>
          </div>
        ))}
      </div>
      {/* Bar clusters — each scales to its own max */}
      <div className="flex items-end justify-center gap-4 sm:gap-10 md:gap-16 max-w-2xl mx-auto">
        {groups.map((g) => (
          <BarCluster key={g.label} label={g.label} bars={g.bars} />
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  NESTED CIRCLES VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function NestedCirclesView({ revenueByArpu, arpuIndex, arpus, setArpuIndex, currency }) {
  const d = revenueByArpu[arpuIndex];

  // Responsive circle diameters
  const [isSmall, setIsSmall] = useState(() => typeof window !== "undefined" && window.innerWidth < 480);
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 480);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const tamD = isSmall ? 150 : 220;
  const samD = isSmall ? 100 : 145;
  const somD = isSmall ? 48 : 68;

  // Container height for circles + external TAM label
  const containerH = tamD + 50;
  // Centre point
  const cx = tamD / 2 + (isSmall ? 20 : 30);
  const cy = containerH / 2 + 8;

  return (
    <>
      {/* ARPU switcher */}
      <div className="flex items-center justify-center gap-1.5 mb-3">
        {arpus.map((a, i) => (
          <button
            key={a}
            onClick={() => setArpuIndex(i)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all ${
              arpuIndex === i
                ? "bg-white/15 text-white border border-white/20"
                : "bg-white/5 text-gray-400 border border-transparent hover:bg-white/10 hover:text-gray-200"
            }`}
          >
            {currency}{a}/m
          </button>
        ))}
      </div>

      {/* Circles — absolutely positioned inside a relative container */}
      <div className="flex justify-center">
        <div className="relative" style={{ width: cx * 2, height: containerH }}>

          {/* ── TAM ring ── */}
          <motion.div
            key={`tam-${arpuIndex}`}
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.45, type: "spring" }}
            className="absolute rounded-full"
            style={{
              width: tamD, height: tamD,
              left: cx - tamD / 2, top: cy - tamD / 2,
              background: `radial-gradient(circle at 35% 35%, ${circleHex.tam}25, ${circleHex.tam}08)`,
              border: `2px solid ${circleHex.tam}40`,
              boxShadow: `0 0 40px ${circleHex.tam}12`,
            }}
          />
          {/* TAM label — above the outer circle */}
          <motion.div
            key={`tam-lbl-${arpuIndex}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute text-center"
            style={{ left: cx - 50, width: 100, top: cy - tamD / 2 - 36 }}
          >
            <span className="block text-[9px] font-bold uppercase tracking-wider text-gray-500">TAM</span>
            <span className="block text-base sm:text-lg font-extrabold text-[#60A5FA]">{d.tam}</span>
          </motion.div>

          {/* ── SAM ring ── */}
          <motion.div
            key={`sam-${arpuIndex}`}
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.08, type: "spring" }}
            className="absolute rounded-full"
            style={{
              width: samD, height: samD,
              left: cx - samD / 2, top: cy - samD / 2,
              background: `radial-gradient(circle at 35% 35%, ${circleHex.sam}30, ${circleHex.sam}10)`,
              border: `2px solid ${circleHex.sam}50`,
              boxShadow: `0 0 24px ${circleHex.sam}12`,
            }}
          />
          {/* SAM label — to the right, outside the ring */}
          <motion.div
            key={`sam-lbl-${arpuIndex}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
            className="absolute"
            style={
              isSmall
                ? { right: 0, top: cy + samD / 2 + 8, textAlign: "right" }
                : { left: cx + samD / 2 + 12, top: cy - 16 }
            }
          >
            <span className="block text-[9px] font-bold uppercase tracking-wider text-gray-500">SAM</span>
            <span className="block text-sm sm:text-base font-extrabold text-[#C4B5FD]">{d.sam}</span>
          </motion.div>
          {/* SAM connector line */}
          {!isSmall && (
            <motion.div
              key={`sam-line-${arpuIndex}`}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
              className="absolute origin-left"
              style={{
                left: cx + samD / 2 + 1,
                top: cy,
                width: 14,
                height: 1,
                background: `${circleHex.sam}60`,
              }}
            />
          )}

          {/* ── SOM ring ── */}
          <motion.div
            key={`som-${arpuIndex}`}
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.45, delay: 0.15, type: "spring" }}
            className="absolute rounded-full"
            style={{
              width: somD, height: somD,
              left: cx - somD / 2, top: cy - somD / 2,
              background: `radial-gradient(circle at 35% 35%, ${circleHex.som}40, ${circleHex.som}14)`,
              border: `2px solid ${circleHex.som}60`,
              boxShadow: `0 0 18px ${circleHex.som}18`,
            }}
          />
          {/* SOM label — inside the innermost circle */}
          <motion.div
            key={`som-lbl-${arpuIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="absolute text-center"
            style={{ left: cx - somD / 2, width: somD, top: cy - 14 }}
          >
            <span className="block text-[8px] font-bold uppercase tracking-wider text-gray-500">SOM</span>
            <span className="block text-xs font-extrabold text-[#6FCFC5]">{d.som}</span>
          </motion.div>

        </div>
      </div>

      {/* Legend below circles */}
      <div className="flex items-center justify-center gap-6 mt-2">
        {[
          { label: "TAM", color: circleHex.tam, val: d.tam },
          { label: "SAM", color: circleHex.sam, val: d.sam },
          { label: "SOM", color: circleHex.som, val: d.som },
        ].map((c) => (
          <div key={c.label} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
            <span className="text-[11px] text-gray-400">
              {c.label}: <span className="text-white font-semibold">{c.val}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function MarketSection({ data }) {
  const rs = data.revenueSizing;
  const currency = rs?.currency || "A$";
  const payerRate = rs?.payerRate || 0.3;
  const arpus = rs?.arpuScenarios || [55, 70, 110];

  const [viewMode, setViewMode] = useState("circles"); // "bars" | "circles"
  const [arpuIndex, setArpuIndex] = useState(1); // default to mid ARPU

  const funnelItems = [
    { tag: "TAM",            desc: "active CFD accounts", ...data.tam },
    { tag: "Adjacent",       desc: "active crypto users", ...data.adjacentMarket },
    { tag: "SAM",            desc: "reachable accounts",  ...data.sam },
    { tag: "SOM (12–18 mo)", desc: "target paid subscriptions", ...data.som },
  ];

  // ── Compute revenue data ──
  const { groups, globalMax, somInsight, revenueByArpu } = useMemo(() => {
    const tamAccounts = data.tam.accounts || 0;
    const samAccounts = data.sam.accounts || 0;
    const somAccounts = data.som.accountsLow || 5_000;

    const calc = (accts, pr) =>
      arpus.map((a) => {
        const v = computeRevenue(accts, pr, a);
        return { value: v, formatted: fmtDollars(v, currency) };
      });

    const tamBars = calc(tamAccounts, payerRate);
    const samBars = calc(samAccounts, payerRate);
    const somBars = calc(somAccounts, 1);

    const allValues = [...tamBars, ...samBars, ...somBars].map((b) => b.value);
    const globalMax = Math.max(...allValues);
    const somMidRev = somBars[1]?.formatted || "";

    // Revenue by ARPU index (for circles view)
    const revenueByArpu = arpus.map((_, i) => ({
      tam: tamBars[i].formatted,
      sam: samBars[i].formatted,
      som: somBars[i].formatted,
    }));

    return {
      groups: [
        { label: "TAM", bars: tamBars },
        { label: "SAM", bars: samBars },
        { label: `SOM (${(somAccounts / 1000).toFixed(0)}K)`, bars: somBars },
      ],
      globalMax,
      somInsight: somMidRev,
      revenueByArpu,
    };
  }, [data, arpus, payerRate, currency]);

  const samPctOfTam = data.tam.accounts && data.sam.accounts
    ? ((data.sam.accounts / data.tam.accounts) * 100).toFixed(0)
    : null;
  const somPctOfSam = data.sam.accounts && data.som.accountsLow
    ? ((data.som.accountsLow / data.sam.accounts) * 100).toFixed(2)
    : null;

  return (
    <PitchSection id="market">
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
        {data.title}
      </h2>

      {/* ────────────── Two-column layout: Funnel + Revenue ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[38%_62%] gap-4 lg:gap-5 items-start">

        {/* ── Left: Subscriber Funnel ── */}
        <div>
          <div className="space-y-1.5 mb-2">
            {funnelItems.map((item, i) => {
              const color = funnelColors[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div
                    className={`${color.bg} ${color.border} border rounded-lg px-3.5 py-2 shadow-md ${color.glow} transition-all hover:scale-[1.01]`}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
                        {item.tag}
                      </span>
                      <span className={`text-lg sm:text-xl font-extrabold ${color.text}`}>
                        {item.value}
                      </span>
                      {item.tooltip && <InfoTooltip>{item.tooltip}</InfoTooltip>}
                    </div>
                    <p className="text-gray-400 text-[10px] leading-snug mt-0.5">{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Conversion funnel percentages */}
          {(samPctOfTam || somPctOfSam) && (
            <div className="flex flex-col gap-0.5 text-[9px] text-gray-500 mt-2">
              {samPctOfTam && (
                <span>TAM → SAM: <span className="text-gray-300 font-semibold">{samPctOfTam}%</span> reachable</span>
              )}
              {somPctOfSam && (
                <span>SAM → SOM: <span className="text-gray-300 font-semibold">{somPctOfSam}%</span> capture target</span>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Revenue Sizing ── */}
        <div className="bg-[#0F1826]/60 border border-white/8 rounded-2xl p-3.5 sm:p-4">
          {/* Header + view toggle */}
          <div className="flex flex-col sm:flex-row items-center justify-between mb-1 gap-2">
            <div className="hidden sm:block flex-1" />
            <div className="text-center sm:flex-1">
              <h3 className="text-sm font-semibold text-white">
                Annual Revenue by ARPU
              </h3>
              <p className="text-[9px] text-gray-500 mt-0.5">
                TAM &amp; SAM: {(payerRate * 100).toFixed(0)}% payer rate · SOM: paid subs (no discount)
              </p>
            </div>
            <div className="sm:flex-1 flex justify-center sm:justify-end">
              <div className="flex items-center bg-white/5 rounded-lg p-0.5">
                <button
                  onClick={() => setViewMode("bars")}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === "bars" ? "bg-white/15 text-white" : "text-gray-500 hover:text-gray-300"
                  }`}
                  title="Bar chart"
                >
                  <BarChart3 size={13} />
                </button>
                <button
                  onClick={() => setViewMode("circles")}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === "circles" ? "bg-white/15 text-white" : "text-gray-500 hover:text-gray-300"
                  }`}
                  title="Nested circles"
                >
                  <Circle size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* Chart area */}
          <div className="mt-2">
            <AnimatePresence mode="wait">
              {viewMode === "bars" ? (
                <motion.div
                  key="bars"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.25 }}
                >
                  <BarChartView groups={groups} arpus={arpus} currency={currency} />
                </motion.div>
              ) : (
                <motion.div
                  key="circles"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  <NestedCirclesView
                    revenueByArpu={revenueByArpu}
                    arpuIndex={arpuIndex}
                    arpus={arpus}
                    setArpuIndex={setArpuIndex}
                    currency={currency}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>

      {/* ────────────── Key Insight Callout ────────────── */}
      <div className="mt-3 max-w-2xl mx-auto bg-gradient-to-r from-[#3B82F6]/10 to-[#4FA9A1]/10 border border-[#3B82F6]/20 rounded-xl px-4 py-2.5 text-center">
        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
          At just <span className="text-white font-semibold">{somPctOfSam || "0.05"}% SAM capture</span> and
          blended ARPU of {currency}65/m, SOM represents{" "}
          <span className="text-[#60A5FA] font-bold">{somInsight} ARR</span> —
          a conservative, bottom-up estimate.
        </p>
      </div>
      {/* Simplified labels below funnel */}
      <div className="max-w-2xl mx-auto mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        {funnelItems.map((item, i) => {
          const color = funnelColors[i];
          return (
            <div key={i} className="text-center">
              <p className={`text-lg font-bold ${color.text}`}>{item.value}</p>
              <p className="text-xs text-gray-400 leading-snug">{item.label}</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-snug">{item.label === "Adjacent" ? "active crypto users" : item.label === "TAM" ? "active CFD accounts" : item.label === "SAM" ? "reachable accounts" : "target subscriptions"}</p>
            </div>
          );
        })}
      </div>

      {/* Assumption footnote */}
      {rs?.payerRateNote && (
        <p className="text-[9px] text-gray-500 text-center mt-2 max-w-xl mx-auto">
          {rs.payerRateNote}
        </p>
      )}
    </PitchSection>
  );
}
